import React from 'react'
import styled from 'styled-components'
import { DSButton, DSText, DSSpacer } from '@zoe/ds-web'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { ContextAwareText } from '../core/text/ContextAwareText'
import { onNextClickType } from '../types'
import { QuizBase } from '../../redux/quiz/types'
import { calculateE7SymptomScore, calculateE7FutureScore, getE7TopSymptoms } from '../../libs/symptomScore/e7Utils'
import { SymptomReductionGraph } from './SymptomReductionGraph'
import { WeightCalculationScreen } from './WeightCalculationScreen'
import { addDays, format } from 'date-fns'
import { useToday } from '../../hooks/useToday'
import { ProjectionGraph, SYMPTOM_LINE, symptomAxis } from './ProjectionGraph'

const HeroImage = styled.img`
  display: block;
  width: 100%;
  border-radius: 16px;
`

// Below this score there is no meaningful symptom reduction to graph, so we show
// the "optimise" / "plan ready" variant instead.
const LOW_SCORE_THRESHOLD = 1

export type FutureScoreScreenE7Props = {
  weightLoss?: boolean
  buttonText?: string
  // e10/e11: use the redesigned ProjectionGraph (and pass it through to the
  // weight-loss low-score screen). Other funnels keep SymptomReductionGraph.
  projectionGraph?: boolean
}

type Props = FutureScoreScreenE7Props & {
  onNextClick: onNextClickType
  quiz: QuizBase
}

const ContinueButton: React.FC<{ buttonText: string; onNextClick: onNextClickType }> = ({
  buttonText,
  onNextClick,
}) => (
  <StickyFooter>
    <StickyFooterContent>
      <DSButton
        variant="primary"
        colorScheme="default"
        size="large"
        width="full"
        onPress={() => (onNextClick as () => void)()}
        testId="continue-button"
      >
        <DSText variant="fluid-paragraph-200" weight="semiBold">
          {buttonText}
        </DSText>
      </DSButton>
    </StickyFooterContent>
  </StickyFooter>
)

export const FutureScoreScreenE7: React.FC<Props> = ({
  weightLoss = false,
  buttonText = 'Continue',
  projectionGraph = false,
  onNextClick,
  quiz,
}) => {
  const score = calculateE7SymptomScore(quiz)
  const projected = calculateE7FutureScore(score)
  const topSymptoms = getE7TopSymptoms(quiz)
  const today = useToday()

  // Score 0-1: no meaningful symptom reduction to plot.
  if (score <= LOW_SCORE_THRESHOLD) {
    // Weight-loss users get the animated weight-prediction graph (the same
    // screen shown earlier as goal-graph) rather than a static image.
    if (weightLoss) {
      return (
        <WeightCalculationScreen
          title="Your health transformation plan is ready"
          description="We predict you'll lose weight with ZOE, while also <b>improving your symptoms</b>, <b>boosting energy</b>, <b>increasing longevity</b>, and much more."
          projectionGraph={projectionGraph}
          onNextClick={onNextClick}
          quiz={quiz}
        />
      )
    }
    // No weight-loss goal: the "optimise" screen with the screen_optimise image.
    return (
      <>
        <ScreenContainer>
          <DSText variant="fluid-heading-700" weight="semiBold">
            Optimise your health with ZOE
          </DSText>
          <DSSpacer size={24} direction="vertical" />
          <HeroImage src="/img/e7/optimise.jpg" alt="" />
          <DSSpacer size={24} direction="vertical" />
          <DSText variant="fluid-paragraph-300" weight="regular">
            <ContextAwareText
              text={
                'ZOE will help you eat the right foods to fuel your unique gut microbiome. No diets, no restrictions.{br}{br}Improving your gut health will keep your inflammation and symptoms low and lead to better, lasting health.'
              }
              quiz={quiz}
            />
          </DSText>
        </ScreenContainer>
        <ContinueButton buttonText={buttonText} onNextClick={onNextClick} />
      </>
    )
  }

  // Graph variant. The no-weight-loss body interpolates the user's top-3 symptoms
  // (lower-cased) into the "[key symptom n]" placeholders from the Figma copy.
  const title = weightLoss
    ? 'You can lose weight and reduce your symptoms by 34% in weeks'
    : 'You can reduce your symptoms by 34% in weeks'

  const symptomList = topSymptoms.map((s) => s.label.toLowerCase())
  const body = weightLoss
    ? '<b>ZOE is proven to work.</b> In our clinical study, participants who followed our personalised food advice saw a 34% decrease in symptoms.{br}{br}' +
      "We'll show you the foods to eat to <b>improve your gut health, lose weight</b> and <b>reduce your symptoms.</b>"
    : '<b>ZOE is backed by science.</b> In our clinical study, participants who followed our personalised food advice saw a 34% decrease in symptoms.{br}{br}' +
      "We'll show you the foods to eat to improve your gut health and reduce your symptoms, including " +
      `<b>${symptomList[0]}</b>, <b>${symptomList[1]}</b>, <b>${symptomList[2]}</b>, and more.`

  return (
    <>
      <ScreenContainer>
        <DSText variant="fluid-heading-600" weight="semiBold">
          {title}
        </DSText>
        <DSSpacer size={24} direction="vertical" />
        {projectionGraph ? (
          <ProjectionGraph
            title="Symptom Severity Score"
            startValue={score}
            endValue={projected}
            yLabels={symptomAxis(score, projected)}
            bubbleText={String(projected)}
            endDateLabel={format(addDays(today, 42), 'd MMM')}
            line={SYMPTOM_LINE}
            dotColor="#F96D63"
            bubbleColor="#FAC800"
          />
        ) : (
          <SymptomReductionGraph startScore={score} endScore={projected} />
        )}
        <DSSpacer size={16} direction="vertical" />
        <DSText variant="fluid-paragraph-300" weight="regular">
          <ContextAwareText text={body} quiz={quiz} />
        </DSText>
        <DSSpacer size={16} direction="vertical" />
        <DSText variant="fluid-label-100" weight="regular" color="placeholder">
          Source: ZOE research in PNS, 2024
        </DSText>
      </ScreenContainer>
      <ContinueButton buttonText={buttonText} onNextClick={onNextClick} />
    </>
  )
}
